import React, { useContext, useEffect, useState } from "react";
import useLabels from "../../../../../customHooks/useLabels";
import {
  GridAlignment,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import {
  AtsSubsidiarySiteEdge,
  AtsSubsidiarySiteOrderField,
  GetSubsidiarySitesResult,
  GetSubsidiarySitesVar,
} from "../../masters/subsidary/queries/types";
import {
  EMPTY_STRING,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../utils/constants";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import { AppContext } from "../../../../../context/context";
import { useLazyQuery } from "@apollo/client";
import { GetSubsidiarySites } from "../../masters/subsidary/queries/queries";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { TableHeaderProps } from "../../../../../utils/types";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import { Title } from "../../../../../stories/Title/Title";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import { Checkbox } from "@mui/material";

interface Props {
  selectedIds: number[];
  handleCheckboxChange: (id: number) => void;
}

const SitesList = ({ selectedIds, handleCheckboxChange }: Props) => {
  const { companyLabel, itemLabel, subsidiaryLabel, subsidiarySiteLabel } =
    useLabels();

  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [subsidiriesSite, setSubsidiriesSite] = useState<
    AtsSubsidiarySiteEdge[]
  >([]);


  const { customer_id } = useUrlParams();
  const { UserRightsConfig } = useAssetTableJson();
  const { state } = useContext(AppContext);

  const [getSubsidiarySites, { data, loading, fetchMore }] = useLazyQuery<
    GetSubsidiarySitesResult,
    GetSubsidiarySitesVar
  >(GetSubsidiarySites);

  useEffect(() => {
    if (customer_id && state.companyId && state.subsidiaryId) {
      getSubsidiarySites({
        variables: {
          
          after: null,
          customerID: customer_id,
          companyID: state.companyId ? state.companyId : 0,
          first: ROWS_PER_PAGE,
          empID: 0,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsSubsidiarySiteOrderField.CREATED_AT,
          },
          name: EMPTY_STRING,
          subsidiaryID: state.subsidiaryId ? state.subsidiaryId : 0,
        },
      });
    }
  }, [getSubsidiarySites, customer_id, state.companyId, state.subsidiaryId]);

  const dynamicHeaders: TableHeaderProps[] = UserRightsConfig.List.map(
    (header) => ({
      headerName: header.headerName,
      className: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (header.field === "checkbox") {
          return (
            <>
              <Checkbox
                value={params.row}
                checked={selectedIds.includes(params.row.subId)}
                onChange={() => handleCheckboxChange(params.row.subId)}
                inputProps={{ "aria-label": "controlled" }}
              />
            </>
          );
        }

        return params.value;
      },
    })
  );
  const columns: GridColDef[] = [...dynamicHeaders];

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetSubsidiarySites.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = subsidiriesSite.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setSubsidiriesSite(updatedNewData);
        setRows(
          updatedNewData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            company_name: node.name,
            company_addr: node.address,
            company_email: node.email,
            company_mobile: node.mobile,
            company_c_person: node.contactPerson,
            subId: node.id,
          }))
        );
      } else {
        setSubsidiriesSite(newData);
        setRows(
          newData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            company_name: node.name,
            company_addr: node.address,
            company_email: node.email,
            company_mobile: node.mobile,
            company_c_person: node.contactPerson,
            subId: node.id,
          }))
        );
      }
      setEndCursor(data.GetSubsidiarySites.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetSubsidiarySites.edges;
              const pageInfo = fetchMoreResult.GetSubsidiarySites.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetSubsidiarySites.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetSubsidiarySites: {
                  edges: [...subsidiriesSite, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetSubsidiarySites.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length) {
      scrollTable.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  return (
    <>
      <Title>{subsidiarySiteLabel}</Title>

      <StyledDatagrid
        columns={columns}
        rows={rows}
        rowHeight={TABLE_ROW_HEIGHT}
        disableRowSelectionOnClick
        hideFooter
      />
    </>
  );
};

export default SitesList;
